import { useCallback, useEffect, useRef, useState } from "react";
import { MessageCircle, HelpCircle, Send, Trash2, Sparkles, Camera, X, ChevronLeft, Clock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { PortalPage, Card, Btn, Spinner, ErrorNote } from "@/components/PortalPage";
import { Tabs } from "@/components/ContentParts";
import { inputCls } from "@/components/AuthShell";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { fileToBase64 } from "@/pages/compliance/Compliance";

const TABS = [{ key: "question", label: "QUESTIONS" }, { key: "chat", label: "CHAT" }];
const ago = (iso) => { const m = Math.round((Date.now() - new Date(iso)) / 60000); return m < 60 ? `${Math.max(1, m)}m` : m < 1440 ? `${Math.round(m / 60)}h` : `${Math.round(m / 1440)}d`; };
const imgSrc = (b64) => (b64?.startsWith("data:") ? b64 : `data:image/jpeg;base64,${b64}`);

const Author = ({ a, at }) => (
  <p className="flex items-center gap-2 text-xs font-bold">
    {a.is_ai ? <><Sparkles size={14} className="text-[var(--accent)]" /> ALR Helper</> : <>{a.first_name}{a.country && <span className="text-neutral-500">· {a.country}</span>}</>}
    <span className="font-normal text-neutral-500">{ago(at)}</span>
  </p>
);
const Pending = ({ status }) => status === "pending_review" ? <p className="mt-1 flex items-center gap-1 text-[11px] text-[#F5A623]"><Clock size={12} /> Waiting for review — only you can see this</p> : null;

const Composer = ({ type, onPosted }) => {
  const ref = useRef(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [image, setImage] = useState(null);
  const [busy, setBusy] = useState(false);
  const post = async () => {
    if (body.trim().length < 2) return;
    setBusy(true);
    try {
      const p = await api("/customer-community/posts", { method: "POST", body: { type, title: type === "question" ? title.trim() || null : null, body: body.trim(), image_b64: image?.b64 || null } });
      setTitle(""); setBody(""); setImage(null);
      if (p.status === "pending_review") toast("Posted — it'll appear for everyone after a quick review.");
      onPosted(p);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Card className="space-y-2" data-testid="cc-composer">
      {type === "question" && <input data-testid="cc-title" className={inputCls} placeholder="Question title (optional)" value={title} onChange={(e) => setTitle(e.target.value)} />}
      <textarea data-testid="cc-body" rows={type === "question" ? 3 : 2} className={inputCls} placeholder={type === "question" ? "Ask other owners and the ALR Helper…" : "Say hi to other machine owners…"} value={body} onChange={(e) => setBody(e.target.value)} />
      {image && <div className="relative w-fit"><img src={image.preview} alt="" className="h-20 rounded-lg" /><button onClick={() => setImage(null)} aria-label="Remove photo" className="absolute -right-2 -top-2 rounded-full bg-black p-1"><X size={12} /></button></div>}
      <input ref={ref} type="file" accept="image/*" hidden data-testid="cc-image-input" onChange={async (e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) setImage({ b64: await fileToBase64(f), preview: URL.createObjectURL(f) }); }} />
      <div className="flex gap-2">
        <button onClick={() => ref.current?.click()} aria-label="Attach photo" data-testid="cc-attach" className="flex h-12 w-12 items-center justify-center rounded-lg border border-[#333]"><Camera size={18} /></button>
        <Btn busy={busy} disabled={body.trim().length < 2} onClick={post} data-testid="cc-post"><Send size={14} /> Post</Btn>
      </div>
    </Card>
  );
};

const Thread = ({ id, onBack, onDeleted }) => {
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api(`/customer-community/posts/${id}`).then(setP).catch((e) => toast.error(e.message)), [id]);
  useEffect(() => { load(); }, [load]);
  const send = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try { await api(`/customer-community/posts/${id}/replies`, { method: "POST", body: { body: reply.trim() } }); setReply(""); await load(); } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const del = async (kind, itemId) => {
    if (!window.confirm("Delete this?")) return;
    try { await api(`/customer-community/${kind}/${itemId}`, { method: "DELETE" }); kind === "posts" ? onDeleted() : load(); } catch (e) { toast.error(e.message); }
  };
  const markAnswered = async () => { try { await api(`/customer-community/posts/${id}/mark-answered`, { method: "POST" }); load(); } catch (e) { toast.error(e.message); } };
  const own = (a) => a.id === user.id || user.role === "admin";
  if (!p) return <Spinner />;
  return (
    <div className="space-y-3" data-testid="cc-thread">
      <button onClick={onBack} data-testid="cc-thread-back" className="flex items-center gap-1 text-sm font-bold text-neutral-400 hover:text-white"><ChevronLeft size={16} /> All posts</button>
      <Card>
        <div className="flex justify-between gap-2"><Author a={p.author} at={p.created_at} />{own(p.author) && <button onClick={() => del("posts", p.id)} aria-label="Delete post" data-testid="cc-delete-post" className="text-neutral-500 hover:text-[#FF3333]"><Trash2 size={16} /></button>}</div>
        {p.title && <p className="mt-2 font-display text-2xl font-bold">{p.title}</p>}
        <p className="mt-2 whitespace-pre-line text-[15px] leading-7">{p.body}</p>
        {p.image_b64 && <img src={imgSrc(p.image_b64)} alt="" className="mt-3 max-h-80 rounded-xl" />}
        <Pending status={p.status} />
        {p.type === "question" && own(p.author) && !p.marked_answered_at && <button onClick={markAnswered} data-testid="cc-mark-answered" className="mt-3 flex items-center gap-1 text-xs font-bold text-[var(--accent)]"><CheckCircle2 size={14} /> Mark answered</button>}
      </Card>
      {p.replies.map((r) => (
        <Card key={r.id} className={`ml-4 p-4 ${r.is_ai ? "border-[var(--accent)]/50" : ""}`} data-testid={`cc-reply-${r.id}`}>
          <div className="flex justify-between gap-2"><Author a={r.author} at={r.created_at} />{own(r.author) && !r.is_ai && <button onClick={() => del("replies", r.id)} aria-label="Delete reply" className="text-neutral-500 hover:text-[#FF3333]"><Trash2 size={14} /></button>}</div>
          <p className="mt-1 whitespace-pre-line text-sm leading-6 text-neutral-200">{r.body}</p>
          <Pending status={r.status} />
        </Card>
      ))}
      <div className="flex gap-2">
        <input data-testid="cc-reply-input" className={inputCls} placeholder="Write a reply…" value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} />
        <button onClick={send} disabled={busy || !reply.trim()} aria-label="Send reply" data-testid="cc-reply-send" className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[var(--accent)] text-black disabled:opacity-40"><Send size={16} /></button>
      </div>
    </div>
  );
};

export default function CustomerCommunity() {
  const [tab, setTab] = useState("question");
  const [posts, setPosts] = useState(null);
  const [open, setOpen] = useState(null);
  const [err, setErr] = useState(null);
  const load = useCallback(() => { setPosts(null); api(`/customer-community/posts?type=${tab}&limit=50`).then(setPosts).catch((e) => setErr(e.message)); }, [tab]);
  useEffect(() => { load(); }, [load]);

  return (
    <PortalPage eyebrow="MACHINE OWNER COMMUNITY" title="Community Chat" testId="customer-community-page">
      {open ? <Thread id={open} onBack={() => { setOpen(null); load(); }} onDeleted={() => { setOpen(null); load(); }} /> : (
        <>
          <Tabs tabs={TABS} active={tab} onChange={setTab} />
          <Composer type={tab} onPosted={(p) => (tab === "question" ? setOpen(p.id) : load())} />
          <ErrorNote>{err}</ErrorNote>
          {!posts && !err && <Spinner />}
          {posts?.length === 0 && <p className="text-sm text-neutral-400" data-testid="cc-empty">Nothing here yet — start the conversation.</p>}
          {posts?.map((p) => (
            <button key={p.id} onClick={() => setOpen(p.id)} data-testid={`cc-post-${p.id}`} className="flex w-full gap-3 rounded-2xl border border-[#333] bg-[#1A1A1A] p-4 text-left hover:border-[var(--accent)]">
              {p.type === "question" ? <HelpCircle size={20} className="mt-0.5 shrink-0 text-[var(--accent)]" /> : <MessageCircle size={20} className="mt-0.5 shrink-0 text-neutral-500" />}
              <span className="min-w-0 flex-1">
                <Author a={p.author} at={p.last_activity_at || p.created_at} />
                {p.title && <span className="mt-1 block font-semibold">{p.title}</span>}
                <span className="mt-1 line-clamp-2 block text-sm text-neutral-400">{p.body}</span>
                <span className="mt-1 flex gap-3 text-[11px] text-neutral-500"><span>{p.reply_count} repl{p.reply_count === 1 ? "y" : "ies"}</span>{p.marked_answered_at && <span className="text-[var(--accent)]">Answered</span>}</span>
                <Pending status={p.status} />
              </span>
            </button>
          ))}
        </>
      )}
    </PortalPage>
  );
}
